import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCreditsView } from '@flobi/asset-library/credits';
import { Icon, ICON_SIZE } from '../chrome/icons';
import { SegmentedControl, Slider, Spinner, Switch } from '../../ui/controls';
import { tileUrl } from '../content/tileUrl';
import type { ScreenBox } from '../useEditorCanvas';
import { terminal, type Binding, type Quote } from '../ai/contracts';
import { runProgress } from '../ai/GenerationCanvasStatus';
import { ADJUST_SECTIONS, NO_ADJUST, autoAdjust, isNeutral, type AdjustValues } from './adjust';
import { EXPAND_RATIOS, toolById, type Strength, type ToolDef } from './catalogue';
import { MaskBrush, type MaskBrushHandle } from './MaskBrush';
import { HIGHLIGHT, type ToolChoices } from './run';
import { UPSCALE_TARGETS, doubled, hasTransparency, startingTarget, upscaled, upscaleFactor, type UpscaleTarget } from './upscaleSizes';
import './imageTools.css';

/*
 * Each picture or clip tool's own screen in the Edit with AI panel (PicturePanel.tsx): Adjust's
 * sliders, Blur's amount, or an AI tool's few controls over ONE button that says its price, with
 * the time under it. What the tool is about to do shows on the canvas, never as a picture in the
 * panel: the brush paints on the picture, Expand draws its new frame around it (Tarek, 15 Oct:
 * "what's the purpose of this top image? I can see it directly in front of me"). While it runs,
 * a bar with the time left and Stop; when it lands, Applied with Compare (hold), Undo, Run again.
 */

export interface ImageToolHost {
  /** The layer's picture (or clip) and its pixel size; null when it is not one, or not saved yet. */
  picture(id: string): { src: string; width: number; height: number; video: boolean; mediaAssetId?: string } | null;
  adjust(id: string): AdjustValues;
  setAdjust(id: string, values: AdjustValues): void;
  /** Focus again at another strength, from the cut-out it already made (no new run, no charge). */
  refocus?(id: string, strength: Strength): Promise<void>;
  blur(id: string): number;
  setBlur(id: string, radius: number): void;
  /** The price and the time a run would take, for these choices. */
  quote(id: string, tool: ToolDef, choices: ToolChoices): Promise<Quote | null>;
  /** Starts the run; answers its key in the file's runs. */
  run(id: string, tool: ToolDef, choices: ToolChoices): Promise<string>;
  /** The tool running on this layer now, if any. */
  running(id: string): string | null;
  /** A run by its key, as the file has it now. */
  runOf(key: string): Binding | null;
  /** Stops a run that has not finished. */
  /** Takes back the last change to the file — the landing, right after it. */
  undo(): void;
  /** Image to Video: Generate opens on Video with this picture as the first frame. */
  toVideo(id: string): void;
  box(id: string): ScreenBox | null;
  onViewChange(listener: () => void): () => void;
  /** Runs started or settled (on this file, by anyone): the screen follows its own run through them. */
  onRunsChange?(listener: () => void): () => void;
  /** A short line for the person (the editor's toast). */
  say(message: string): void;
}

/** One tool's own screen. */
export function ToolView({ id, tool, host, picture, words }: { id: string; tool: ToolDef; host: ImageToolHost; picture: NonNullable<ReturnType<ImageToolHost['picture']>>; /** Words to start with (from the Generate panel's suggestion). */ words?: string }) {
  if (tool.id === 'adjust') return <AdjustView id={id} host={host} picture={picture} />;
  if (tool.id === 'blur') return <BlurView id={id} host={host} />;
  return <AiView id={id} tool={tool} host={host} picture={picture} words={words} />;
}

/**
 * A slider's writes, at most one a frame — the last value of each — and the last one on close.
 * Pointer events come faster than the screen draws (120 a second on a ProMotion trackpad), and
 * each write is a document edit every collaborator receives.
 */
function useFrameWrites<T>(write: (value: T) => void): (value: T) => void {
  const latest = useRef(write);
  latest.current = write;
  const pending = useRef<{ value: T } | null>(null);
  const frame = useRef(0);
  useEffect(() => () => {
    cancelAnimationFrame(frame.current);
    if (pending.current) latest.current(pending.current.value);
  }, []);
  return useCallback((value: T) => {
    pending.current = { value };
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      const next = pending.current; pending.current = null;
      if (next) latest.current(next.value);
    });
  }, []);
}

function AdjustView({ id, host, picture }: { id: string; host: ImageToolHost; picture: { src: string; video: boolean } }) {
  const [values, setValues] = useState<AdjustValues>(() => host.adjust(id));
  const [busy, setBusy] = useState(false);
  const write = useFrameWrites<AdjustValues>(next => host.setAdjust(id, next));
  const change = (next: AdjustValues) => { setValues(next); write(next); };
  async function auto() {
    if (picture.video) return;
    setBusy(true);
    try {
      const image = new Image(); image.crossOrigin = 'anonymous'; image.src = tileUrl(picture.src);
      await image.decode();
      const c = document.createElement('canvas'); c.width = 96; c.height = Math.max(1, Math.round(96 * image.naturalHeight / image.naturalWidth));
      const g = c.getContext('2d')!; g.drawImage(image, 0, 0, c.width, c.height);
      change(autoAdjust(g.getImageData(0, 0, c.width, c.height).data));
    } catch { /* a picture that cannot be read leaves the sliders as they are */ }
    finally { setBusy(false); }
  }
  return <div className="image-tools-body ui-scroll">
    <p className="image-tools-line">{toolById('adjust').line}</p>
    {!picture.video && <button type="button" className="image-tools-primary" disabled={busy} onClick={() => void auto()}>{busy ? <Spinner size={12} /> : <Icon name="sparkles" size={ICON_SIZE.small} />}Auto Adjust</button>}
    {ADJUST_SECTIONS.map(section => <section key={section.id} className="image-tools-group">
      <h4>{section.label}</h4>
      {section.controls.map(control => <label key={control.id} className="image-tools-slider">
        <span>{control.label}</span>
        <Slider label={control.label} min={control.min} max={control.max} value={values[control.id]} resetTo={0} track={control.id === 'temperature' ? 'linear-gradient(90deg,#7aa2ff,#e8e2d6,#f0b45e)' : control.id === 'tint' ? 'linear-gradient(90deg,#8fe08a,#e6e1e6,#d48ce8)' : undefined}
          onChange={value => change({ ...values, [control.id]: value })} />
        <output className="tnum">{values[control.id]}</output>
      </label>)}
    </section>)}
    <button type="button" className="image-tools-secondary" disabled={isNeutral(values)} onClick={() => change({ ...NO_ADJUST })}>Reset Adjustments</button>
  </div>;
}

function BlurView({ id, host }: { id: string; host: ImageToolHost }) {
  const [radius, setRadius] = useState(() => host.blur(id));
  const write = useFrameWrites<number>(next => host.setBlur(id, next));
  return <div className="image-tools-body">
    <p className="image-tools-line">{toolById('blur').line}</p>
    <label className="image-tools-slider"><span>Amount</span>
      <Slider label="Blur amount" min={0} max={100} value={radius} resetTo={0} onChange={value => { setRadius(value); write(value); }} />
      <output className="tnum">{radius}</output></label>
  </div>;
}

/** Landings that keep the layer where it is, so the picture as it was can be held over it. */
const COMPARABLE: readonly string[] = ['replace', 'expand', 'focus', 'vector', 'cut-out'];
const STRENGTHS: readonly { value: Strength; label: string }[] = [{ value: 'light', label: 'Light' }, { value: 'natural', label: 'Natural' }, { value: 'strong', label: 'Strong' }];
const COUNTS = [2, 3, 4, 5, 6, 8] as const;

function AiView({ id, tool, host, picture, words }: { id: string; tool: ToolDef; host: ImageToolHost; picture: { src: string; width: number; height: number; video: boolean; mediaAssetId?: string }; words?: string }) {
  const asks = tool.asks ?? NO_ASKS;
  const [typed, setTyped] = useState(() => (asks.includes('prompt') && words ? words : ''));
  const [ratio, setRatio] = useState<string>(EXPAND_RATIOS.find(r => Math.abs(ratioOf(r) - picture.width / picture.height) > 0.02) ?? '1:1');
  const [count, setCount] = useState(4);
  const [strength, setStrength] = useState<Strength>('natural');
  const [refocusing, setRefocusing] = useState(false);
  // Upscale's size, once the picture is known not to need the model that keeps transparency.
  const sized = tool.id === 'upscale';
  const [transparent, setTransparent] = useState<boolean | null>(null);
  const [wanted, setWanted] = useState<UpscaleTarget | null>(null);
  const target = !sized || transparent !== false ? null : wanted && upscaleFactor(wanted, picture.width, picture.height) ? wanted : startingTarget(picture.width, picture.height);
  const unsized = sized && (transparent === null || (transparent === false && !target));
  useEffect(() => {
    if (!sized) return;
    let live = true; setTransparent(null);
    void hasTransparency(picture.src, picture.mediaAssetId).then(value => { if (live) setTransparent(value); });
    return () => { live = false; };
  }, [sized, picture.src, picture.mediaAssetId]);
  const [brushing, setBrushing] = useState(tool.id === 'erase');
  const [painted, setPainted] = useState(false);
  const [brush, setBrush] = useState(36);
  const [quote, setQuote] = useState<Quote | null>(null);
  const credits = useCreditsView();
  const [problem, setProblem] = useState('');
  const [sending, setSending] = useState(false);
  // The run this screen started, and the picture as it was before it, for Compare.
  const [runKey, setRunKey] = useState<string | null>(null);
  const before = useRef('');
  const [landed, setLanded] = useState<string | null>(null);
  const [comparing, setComparing] = useState(false);
  const mask = useRef<MaskBrushHandle>(null);
  const [, refresh] = useState(0);
  useEffect(() => host.onRunsChange?.(() => refresh(n => n + 1)), [host]);
  const run = runKey ? host.runOf(runKey) : null;
  const working = !!run && !terminal(run.status);
  // The bar moves on its own clock while the run works.
  useEffect(() => {
    if (!working) return;
    const timer = setInterval(() => refresh(n => n + 1), 250);
    return () => clearInterval(timer);
  }, [working]);
  // What became of the run: landed (Applied), or not (said once, the button back).
  useEffect(() => {
    if (!run || !runKey || !terminal(run.status)) return;
    if (run.status === 'completed' && run.applied) { if (run.message) setProblem(run.message); else setLanded(runKey); setRunKey(null); }
    else if (run.status !== 'completed') { setProblem(run.status === 'failed' ? (run.message || `${tool.label} didn’t finish. You were not charged.`) : ''); setRunKey(null); }
  }, [run, runKey, tool.label]);
  const otherRun = host.running(id);
  const busyElsewhere = !!otherRun && !working;
  const needsWords = asks.includes('prompt') && !typed.trim();
  const needsArea = brushing && !painted;
  const why = needsWords ? 'Say what to change first' : needsArea ? (tool.id === 'erase' ? 'Brush over something first' : 'Brush an area, or turn the switch off') : sized && transparent === false && !target ? 'Already 4K or larger' : '';
  const choices = useMemo<ToolChoices>(() => ({ typed, ...(asks.includes('ratio') ? { ratio } : {}), ...(asks.includes('count') ? { count } : {}), ...(tool.id === 'retouch' || tool.id === 'focus' ? { strength } : {}), ...(target ? { target } : {}) }), [typed, ratio, count, strength, asks, tool.id, target]);
  // The price and time follow the choices (a mask does not change them); asked after typing settles.
  useEffect(() => {
    let live = true; setQuote(null);
    if (unsized) return;
    const timer = setTimeout(() => host.quote(id, tool, choices).then(value => { if (live) setQuote(value); })
      // A refusal of ours (a clip too long for the model) is said before anyone presses.
      .catch(error => { if (live && error instanceof Error && error.message.startsWith(tool.label)) setProblem(error.message); }), 350);
    return () => { live = false; clearTimeout(timer); };
  }, [id, tool, choices, host, unsized]);
  const onPainted = useCallback((value: boolean) => setPainted(value), []);
  const box = useBox(id, host, brushing || tool.id === 'expand' || comparing);

  async function go() {
    setSending(true); setProblem(''); setLanded(null);
    try {
      const blob = brushing && painted ? await mask.current?.mask(picture.width, picture.height, HIGHLIGHT) : null;
      before.current = host.picture(id)?.src ?? '';
      const key = await host.run(id, tool, { ...choices, mask: blob ?? null });
      mask.current?.clear();
      setRunKey(key);
    } catch (error) { setProblem(error instanceof Error ? error.message : 'That could not start.'); }
    finally { setSending(false); }
  }
  const after = target ? upscaled(picture.width, picture.height, upscaleFactor(target, picture.width, picture.height)!) : transparent ? doubled(picture.width, picture.height) : null;
  const verb = tool.id === 'expand' ? `Expand to ${ratio}` : tool.id === 'split' ? `Split into ${count} layers` : target ? `Upscale to ${target}` : tool.label;
  const progress = run && working ? runProgress(run, undefined, Date.now()) : null;
  const left = run && working && run.etaSeconds ? Math.max(0, Math.round(run.etaSeconds - (Date.now() - run.createdAt) / 1000)) : null;
  const brushControls = <div className="image-tools-brush">
    <label className="image-tools-slider"><span>Brush</span><Slider label="Brush size" min={8} max={160} value={brush} onChange={setBrush} /><output className="tnum">{brush}</output></label>
    <p className="image-tools-hint">{painted ? 'Brush more, or clear it.' : `Brush over ${tool.id === 'erase' ? 'what to remove' : 'the area to change'}.`}{painted && <button type="button" className="image-tools-link" onClick={() => mask.current?.clear()}>Clear</button>}</p>
  </div>;
  return <div className="image-tools-body">
    {tool.id === 'erase' && brushControls}
    {tool.id === 'retouch' && <SegmentedControl label="Strength" size="small" segments={STRENGTHS} value={strength} onChange={setStrength} />}
    {/* Focus's blur: chosen before it runs; once it has landed, another one is composed again from
        the same cut-out, free (the picture is one layer, so a live blur would blur the subject too). */}
    {tool.id === 'focus' && <SegmentedControl label="Background blur" size="small" segments={STRENGTHS} value={strength} onChange={next => {
      const was = strength; setStrength(next);
      if (landed && host.refocus) { setProblem(''); setRefocusing(true); host.refocus(id, next).catch(error => { setStrength(was); setProblem(error instanceof Error ? error.message : 'That could not be changed.'); }).finally(() => setRefocusing(false)); }
    }} />}
    {refocusing && <p className="image-tools-running"><Spinner size={12} />Blurring the background again…</p>}
    {sized && transparent !== true && <SegmentedControl label="Size" size="small" value={target} onChange={setWanted}
      segments={UPSCALE_TARGETS.map(value => ({ value, label: value, disabled: !upscaleFactor(value, picture.width, picture.height) }))} />}
    {sized && <p className="image-tools-sizes"><b>{picture.width} × {picture.height}</b>{after && <><span aria-hidden>→</span><b>{after.width} × {after.height}</b></>}</p>}
    {asks.includes('prompt') && <>
      <textarea className="image-tools-words" rows={3} value={typed} maxLength={1500} onChange={event => setTyped(event.target.value)}
        placeholder={tool.id === 'new-background' ? 'A sunlit marble counter, soft morning light' : 'Make the jacket red'} />
      {tool.examples && <div className="image-tools-chips" role="group" aria-label="Examples">{tool.examples.map(example => <button key={example} type="button" aria-pressed={typed === example} onClick={() => setTyped(example)}>{example}</button>)}</div>}
    </>}
    {asks.includes('area') && tool.id !== 'erase' && <div className="image-tools-switch"><span>Only an area I brush</span><Switch label="Only an area I brush" checked={brushing} onChange={setBrushing} /></div>}
    {tool.id !== 'erase' && brushing && brushControls}
    {asks.includes('ratio') && <div className="image-tools-chips" role="group" aria-label="New shape">{EXPAND_RATIOS.map(r => <button key={r} type="button" aria-pressed={ratio === r} onClick={() => setRatio(r)}><ShapeMark ratio={r} />{r}</button>)}</div>}
    {asks.includes('count') && <div className="image-tools-chips" role="group" aria-label="Layers">{COUNTS.map(n => <button key={n} type="button" aria-pressed={count === n} onClick={() => setCount(n)}>{n}</button>)}</div>}
    {problem && <p className="image-tools-problem" role="alert">{problem}</p>}
    {working && run ? <div className="image-tools-run">
      <div className="image-tools-bar"><i style={{ width: `${progress?.percent ?? 0}%` }} /></div>
      <p className="image-tools-run-line"><span>{run.status === 'requesting' ? 'Sending the picture…' : run.status === 'saving' ? 'Saving to My Stuff…' : left !== null ? `${tool.label}… about ${left} s left` : `${tool.label}…`}</span></p>
    </div>
    : landed ? <div className="image-tools-applied">
      <span className="image-tools-ok"><Icon name="check" size={14} />Applied</span>
      {COMPARABLE.includes(tool.land ?? '') && before.current && <button type="button" aria-label="Compare with before" aria-pressed={comparing} title="Hold to see the picture as it was"
        onPointerDown={() => setComparing(true)} onPointerUp={() => setComparing(false)} onPointerLeave={() => setComparing(false)}><Icon name="eye" size={14} /></button>}
      <button type="button" onClick={() => { host.undo(); setLanded(null); setComparing(false); }}>Undo</button>
      <button type="button" onClick={() => void go()}>Run again</button>
    </div>
    : busyElsewhere ? <p className="image-tools-running"><Spinner size={12} />{otherRun} is working on this {picture.video ? 'clip' : 'picture'}.</p>
    : <div className="image-tools-go">
      <button type="button" className="image-tools-primary" disabled={sending || !!why || unsized} onClick={() => void go()}>
        {sending ? <Spinner size={12} /> : <Icon name={tool.icon} size={ICON_SIZE.small} />}<span>{verb}</span>{quote && <small className="tnum">· {credits.label(quote.credits)}</small>}</button>
      {/* The reason the button is off, or where the result lands; the time on the right. Kept while the quote is asked, so nothing moves. */}
      <p className="image-tools-meta tnum"><span>{why || (sized && transparent ? 'Doubled, to keep the transparency' : tool.caption) || ' '}</span><span>{quote?.seconds ? `~${timeWord(quote.seconds)}` : ' '}</span></p>
    </div>}
    {brushing && !working && !landed && box && <MaskBrush ref={mask} box={box} size={brush} onPainted={onPainted} />}
    {tool.id === 'expand' && !working && !landed && box && <ExpandFrame box={box} ratio={ratio} />}
    {comparing && landed && box && <Before box={box} src={before.current} />}
  </div>;
}

/** A stable empty list: a new [] each render made the quote's inputs new each render, and it never landed. */
const NO_ASKS: NonNullable<ToolDef['asks']> = [];
const ratioOf = (r: string) => { const [w, h] = r.split(':').map(Number); return w! / h!; };
const timeWord = (seconds: number) => seconds >= 90 ? `${Math.round(seconds / 60)} min` : `${Math.round(seconds)} s`;

/** The shape, drawn on its chip. */
function ShapeMark({ ratio }: { ratio: string }) {
  const [w, h] = ratio.split(':').map(Number);
  const k = 11 / Math.max(w!, h!);
  return <i className="image-tools-shape" style={{ width: Math.max(4, w! * k), height: Math.max(4, h! * k) }} aria-hidden />;
}

/** Expand's intent, on the canvas: the new frame about the picture's middle, as `grown` will make it. */
function ExpandFrame({ box, ratio }: { box: ScreenBox; ratio: string }) {
  const w = Math.max(1, box.right - box.left), h = Math.max(1, box.bottom - box.top);
  const r = ratioOf(ratio), frame = w / h;
  if (Math.abs(frame - r) <= 0.01 * frame) return null;
  const W = r > frame ? h * r : w, H = r > frame ? h : w / r;
  return createPortal(<div className="artwork-expand-frame" style={{ left: box.left + (w - W) / 2, top: box.top + (h - H) / 2, width: W, height: H }} aria-hidden><span>{ratio}</span></div>, document.body);
}

/** The picture as it was, held over where it is now. */
function Before({ box, src }: { box: ScreenBox; src: string }) {
  return createPortal(<div className="artwork-before" style={{ left: box.left, top: box.top, width: Math.max(1, box.right - box.left), height: Math.max(1, box.bottom - box.top) }} aria-hidden>
    <img src={tileUrl(src, 1600)} alt="" />
  </div>, document.body);
}

/** The layer's box on screen, following the camera — only while something is drawn over it. */
function useBox(id: string, host: ImageToolHost, active: boolean): ScreenBox | null {
  const [box, setBox] = useState<ScreenBox | null>(null);
  useEffect(() => {
    if (!active) { setBox(null); return; }
    const measure = () => setBox(host.box(id));
    measure();
    return host.onViewChange(measure);
  }, [id, host, active]);
  return box;
}
